/**
 * Pure radar-lane assignment for the Attention Radar board.
 * Each item lands in at most one lane so the board stays honest as status changes.
 */

import { AgentItem, AttentionItem, Tone } from './types';

export type RadarLaneId = 'now' | 'waiting-you' | 'ready' | 'stuck' | 'release';

export interface RadarLaneDef {
  id: RadarLaneId;
  title: string;
  tone: Tone;
  hint: string;
}

export const RADAR_LANES: RadarLaneDef[] = [
  {
    id: 'now',
    title: 'En curso ahora',
    tone: 'accent',
    hint: 'tus tareas vivas y su avance real',
  },
  {
    id: 'waiting-you',
    title: 'Te esperan',
    tone: 'warn',
    hint: 'no avanza hasta que respondas',
  },
  {
    id: 'ready',
    title: 'Listo para cerrar',
    tone: 'ok',
    hint: 'un clic y fuera del tablero',
  },
  {
    id: 'stuck',
    title: 'Atascado',
    tone: 'bad',
    hint: 'necesita que empujes fuera del equipo',
  },
  {
    id: 'release',
    title: 'Esperando release',
    tone: 'flat',
    hint: 'aprobado, en cola para la ventana',
  },
];

function normalizeWorkflow(workflow?: string): string {
  return (workflow || '').trim().toLowerCase();
}

/**
 * Assigns an attention item to a radar lane, or null when it is waiting on others
 * and should not occupy the user's attention board.
 */
export function assignRadarLane(item: AttentionItem): RadarLaneId | null {
  if (item.st === 'blocked' || item.blocksWork) {
    return 'stuck';
  }
  if (item.st === 'ready') {
    return 'ready';
  }

  const workflow = normalizeWorkflow(item.workflow);
  if (item.merged || workflow === 'done' || workflow === 'in qa') {
    return 'release';
  }

  if (item.source === 'jira' && ['in dev', 'en curso', 'pull request'].includes(workflow)) {
    return 'now';
  }

  if (item.st === 'you' || item.source === 'gmail') {
    return 'waiting-you';
  }

  return null;
}

export function agentsForLane(lane: RadarLaneId, agents: AgentItem[]): AgentItem[] {
  if (lane === 'now') {
    return agents.filter((agent) => agent.running);
  }
  if (lane === 'waiting-you') {
    return agents.filter((agent) => !agent.running);
  }
  return [];
}

export function countRadarSignals(items: AttentionItem[], agents: AgentItem[]): number {
  const itemCount = items.filter((item) => assignRadarLane(item) !== null).length;
  return itemCount + agents.length;
}
